import * as React from "react";
import { useFormContext } from "react-hook-form";
import { ImageFlux } from "@components/ImageFlux";
import ReservationFrameStateLabel from "@components/ReservationFrameItem/ReservationFrameStateLabel";
import type {
  ReservationFrameItem_PointsToGiveFrameFragment,
  ReservationFrameItem_ReservationFrameFragment,
} from "@components/ReservationFrameItem/__generated__/fragments";
import { ReservationFrameStateTypeEnum } from "@graphql/__generated__/types";
import useIsPcByUA from "@hooks/useIsPcByUA";
import { toJpTime } from "@libs/utils/date";
import {
  buildCancelPolicyText,
  cancelPolicyTitleWithDateSwitchNote,
} from "@libs/utils/reservation";
import styles from "./index.module.css";

type Props = {
  frame: ReservationFrameItem_ReservationFrameFragment;
  pointsToGiveFrame: ReservationFrameItem_PointsToGiveFrameFragment | undefined;
  onTelStateItemClick: () => void;
  cancelFeePercentage: number | undefined;
};

const ReservationFrameItem: React.FC<Props> = ({
  frame,
  pointsToGiveFrame,
  onTelStateItemClick,
  cancelFeePercentage,
}) => {
  const { isPcByUA } = useIsPcByUA.useContainer();
  const { register } = useFormContext();

  const canSelect =
    frame.isInReservableTime &&
    frame.state === ReservationFrameStateTypeEnum.Active;
  const canTel =
    frame.state === ReservationFrameStateTypeEnum.Restricted && !isPcByUA;
  const canClick = canSelect || canTel;

  const isActiveOutdated =
    !frame.isInReservableTime &&
    frame.state === ReservationFrameStateTypeEnum.Active;

  const plan = frame.reservationPlan;
  const isPersonNumSingle = plan.minPersonNum === plan.maxPersonNum;

  const cancelPolicyRefundPercentage = plan?.cancelPolicy?.refundPercentage;
  const cancelPolicyText =
    cancelPolicyRefundPercentage &&
    cancelFeePercentage &&
    buildCancelPolicyText(
      cancelPolicyRefundPercentage,
      cancelFeePercentage,
      frame.date
    );

  const handleClosePlanDetails = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.currentTarget.parentElement?.removeAttribute("open");
  };

  return (
    <div className={styles.planCardRadio}>
      <input
        {...register("frameId")}
        type="radio"
        value={frame.id}
        id={`plan-${frame.id}`}
        disabled={!canClick}
      />
      <label
        htmlFor={`plan-${frame.id}`}
        onClick={(event): void => {
          if (canTel) {
            event.preventDefault();
            onTelStateItemClick();
          }
        }}
      >
        <div className={styles.planCardRadioLabel}>
          <div className={styles.planCardRadioInner}>
            <div className={styles.planInfo}>
              <ReservationFrameStateLabel
                state={isActiveOutdated ? null : frame.state}
              />

              <div className={plan.imageUrl ? styles.planNameWrapper : ""}>
                {plan.imageUrl && (
                  <div className={styles.planThumbnail}>
                    <ImageFlux
                      imageType="thumbnail"
                      url={plan.imageUrl}
                      alt={plan.name}
                    />
                  </div>
                )}
                <div className={styles.planName}>{plan.name}</div>
              </div>
              <div className={styles.priceTermsWrapper}>
                {plan.totalFee > 0 && (
                  <div className={styles.planPrice}>
                    <em>{plan.totalFee.toLocaleString()}</em>円(税込)
                  </div>
                )}
                <ul className={styles.planTerms}>
                  <li className={styles.number}>
                    {isPersonNumSingle
                      ? `${plan.minPersonNum}名`
                      : `${plan.minPersonNum}〜${plan.maxPersonNum}名`}
                  </li>
                  <li className={styles.time}>
                    {`${toJpTime(frame.startsAt ?? "")}〜${toJpTime(
                      frame.endsAt ?? ""
                    )}`}
                  </li>
                </ul>
              </div>
              {plan.contentChargeFee >= 1 && plan.fee === 0 && (
                <div className={styles.priceTermsNotes}>
                  <p className={styles.priceTermsNotesText}>
                    ※飲食代は含まれていません
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </label>
      <details className={styles.planDetails}>
        <summary className={styles.planDetailsSummary}>
          <div className={styles.title}>プランの詳細</div>
        </summary>
        <div
          className={styles.planDetailsCont}
          onClick={(e) => {
            handleClosePlanDetails(e);
          }}
        >
          {plan.totalFee > 0 && (
            <dl>
              <dt>プラン料金内訳</dt>
              {plan.fee > 0 && (
                <dd>
                  {!plan.isEvent ? "飲食代" : "チケット代"}：
                  {plan.fee.toLocaleString()}円
                </dd>
              )}
              {plan.contentChargeFee > 0 && (
                <dd>
                  コンテンツチャージ：{plan.contentChargeFee.toLocaleString()}円
                </dd>
              )}
            </dl>
          )}
          <dl>
            <dt>プラン内容</dt>
            <dd>{plan.description}</dd>
          </dl>
          {plan.notes && (
            <dl>
              <dt>
                {plan.cancelPolicy
                  ? "注意事項"
                  : "注意事項・キャンセルポリシー"}
              </dt>
              <dd>{plan.notes}</dd>
            </dl>
          )}
          {plan.cancelPolicy && !plan.isEvent && (
            <dl>
              <dt>{cancelPolicyTitleWithDateSwitchNote}</dt>
              <dd>{cancelPolicyText}</dd>
            </dl>
          )}
          {pointsToGiveFrame && (
            <dl>
              <dt>獲得ポイント内訳</dt>
              <dd>
                <div className={styles.point}>
                  <div className={styles.pointItem}>
                    <div className={styles.pointItemTitle}>WEB予約</div>
                    <div className={styles.pointItemData}>
                      <ul>
                        {pointsToGiveFrame.webOnSitePayment && (
                          <li>
                            現地決済 最大{pointsToGiveFrame.webOnSitePayment}pt
                          </li>
                        )}
                        {pointsToGiveFrame.webOnSystemPayment && (
                          <li>
                            事前決済 最大{pointsToGiveFrame.webOnSystemPayment}
                            pt
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  <div className={styles.pointItem}>
                    <div className={styles.pointItemTitle}>アプリ予約</div>
                    <div className={styles.pointItemData}>
                      <ul>
                        {pointsToGiveFrame.appOnSitePayment && (
                          <li>
                            現地決済 最大{pointsToGiveFrame.appOnSitePayment}pt
                          </li>
                        )}
                        {pointsToGiveFrame.appOnSystemPayment && (
                          <li>
                            事前決済 最大{pointsToGiveFrame.appOnSystemPayment}
                            pt
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </dd>
            </dl>
          )}
        </div>
      </details>
    </div>
  );
};

export default ReservationFrameItem;
